import React from 'react';
import { formatDate } from '../utils/dateUtils';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { printRentalInvoice, createRentalInvoiceData } from '../utils/invoiceUtils';
import { RentalRecord } from '../types';
import { BrandLogo } from './BrandLogo';

interface RentalDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: RentalRecord | null;
  carInfo: {
    brandId: string;
    brand: string;
    model: string;
    plate?: string;
    year?: number;
    color?: string;
  } | null;
}

export const RentalDetailModal: React.FC<RentalDetailModalProps> = ({
  isOpen,
  onClose,
  record,
  carInfo
}) => {
  if (!isOpen || !record || !carInfo) return null;

  const totalWithCharges = record.totalAmount + (record.additionalCharges?.reduce((sum, charge) => sum + charge.amount, 0) || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-semibold">Rental Details</h2>
          <p className="text-blue-100 text-sm mt-1">
            Complete rental information
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Car Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10c-.1-.1-.4-.6-1.4-1.3C16.1 8.4 14.7 8 13 8H9.5c-.6 0-1.1.4-1.4.9L6.8 11H4c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h2c0 1.7 1.3 3 3 3s3-1.3 3-3h4c0 1.7 1.3 3 3 3s3-1.3 3-3z"/>
              </svg>
              Vehicle Information
            </h3>
            <div className="flex items-center space-x-4">
              <BrandLogo brandId={carInfo?.brandId || ''} size="lg" />
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">
                  {carInfo.brand} {carInfo.model}
                </h4>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  {carInfo.plate && <span>Plate: {carInfo.plate}</span>}
                  {carInfo.year && <span>Year: {carInfo.year}</span>}
                  {carInfo.color && <span className="capitalize">Color: {carInfo.color}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Name:</span>
                <span className="ml-2 text-blue-800">{record.customerName || 'N/A'}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Contact:</span>
                <span className="ml-2 text-blue-800">{record.customerContact || 'N/A'}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Email:</span>
                <span className="ml-2 text-blue-800">{record.customerEmail || 'N/A'}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">License:</span>
                <span className="ml-2 text-blue-800">{record.customerLicense || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Rental Period */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-medium text-green-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Rental Period
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700 font-medium">Start Date:</span>
                <span className="ml-2 text-green-800">{formatDate(record.startDate)}</span>
              </div>
              <div>
                <span className="text-green-700 font-medium">End Date:</span>
                <span className="ml-2 text-green-800">{formatDate(record.endDate)}</span>
              </div>
              <div>
                <span className="text-green-700 font-medium">Duration:</span>
                <span className="ml-2 text-green-800">{record.totalDays} day{record.totalDays === 1 ? '' : 's'}</span>
              </div>
              <div>
                <span className="text-green-700 font-medium">Total Amount:</span>
                <span className="ml-2 text-green-800 font-semibold">{formatSimpleCurrency(record.totalAmount, true)}</span>
              </div>
            </div>
          </div>

          {/* Mileage Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Mileage Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-700 font-medium">Start Mileage:</span>
                <span className="ml-2 text-gray-800">{record.startMileage.toLocaleString()} km</span>
              </div>
              <div>
                <span className="text-gray-700 font-medium">End Mileage:</span>
                <span className="ml-2 text-gray-800">{record.endMileage.toLocaleString()} km</span>
              </div>
              <div>
                <span className="text-gray-700 font-medium">Distance Driven:</span>
                <span className="ml-2 text-gray-800 font-semibold">{(record.endMileage - record.startMileage).toLocaleString()} km</span>
              </div>
            </div>
          </div>

          {/* Additional Charges */}
          {record.additionalCharges && record.additionalCharges.length > 0 && (
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="font-medium text-orange-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Additional Charges
              </h3>
              <div className="space-y-2">
                {record.additionalCharges.map((charge, index) => (
                  <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-orange-100">
                    <div>
                      <span className="font-medium text-orange-800 capitalize">{charge.type}:</span>
                      <span className="ml-2 text-orange-700">{charge.description}</span>
                    </div>
                    <span className="font-semibold text-orange-800">{formatSimpleCurrency(charge.amount, true)}</span>
                  </div>
                ))}
                <div className="border-t border-orange-200 pt-2 mt-2">
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span className="text-orange-800">Total Additional Charges:</span>
                    <span className="text-orange-800">{formatSimpleCurrency(record.additionalCharges.reduce((sum, charge) => sum + charge.amount, 0), true)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Final Total */}
          <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-primary-800">Final Total:</span>
              <span className="text-2xl font-bold text-primary-900">{formatSimpleCurrency(totalWithCharges, true)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-2xl">
          <button
            onClick={() => {
              if (record && carInfo) {
                const invoiceData = createRentalInvoiceData(record, carInfo);
                printRentalInvoice(invoiceData);
              }
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Print Invoice</span>
          </button>
          
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
